import * as go from 'gojs';
import type { AvailablePort } from 'gojs/types';
import theme from 'theme/theme';
import {
  SMART_SELECT_BUTTON_HEIGHT,
  SMART_SELECT_BUTTON_WIDTH,
  SMART_SELECT_CAPACITY_TEXT_NAME,
  SMART_SELECT_CLOSE_ICON_SOURCE,
  SMART_SELECT_ICON_SOURCE,
  SMART_SELECT_NEXT_PAGE_NAME,
  SMART_SELECT_OPTION_CARD_HEIGHT,
  SMART_SELECT_OPTIONS_PER_PAGE,
  SMART_SELECT_PAGINATION_PANEL_NAME,
  SMART_SELECT_OUTPUT_PILLS_PANEL_NAME,
  SMART_SELECT_PANEL_HEIGHT,
  SMART_SELECT_PANEL_WIDTH,
  SMART_SELECT_PARTICLE_SIZE_TEXT_NAME,
  SMART_SELECT_PLACE_BUTTON_FILL,
  SMART_SELECT_PREVIOUS_PAGE_NAME,
  SMART_SELECT_RECOMMENDED_OPTIONS_PANEL_NAME,
  SMART_SELECT_USE_BUTTON_SHAPE_NAME,
  SMART_SELECT_USE_BUTTON_TEXT_NAME,
} from './smartSelectConstants';
import {
  getArrowPoints,
  getSmartSelectButtonPosition,
  getSmartSelectPanelDimensions,
  getSmartSelectPanelPosition,
} from './smartSelectGeometry';
import {
  createSmartSelectOptionCards,
  type SmartSelectOptionActions,
} from './smartSelectOptionCards';
import {
  getSmartSelectCapacity,
  getSmartSelectOutputPorts,
  getSmartSelectParticleSize,
  getSmartSelectPortLabel,
} from './smartSelectPorts';
import {
  getCachedSmartSelectOptions,
  getSmartSelectRecommendationStatus,
} from './smartSelectRecommendationLoader';
import type { SmartSelectRecommendedOption } from './smartSelectRecommendations';
import {
  clearTimer,
  getSelectedSmartSelectPortKey,
  hideTimers,
  nodeSmartSelectPanels,
  nodeSmartSelectSelectedPortKeys,
} from './smartSelectState';

const $ = go.GraphObject.make;

const USE_BUTTON_WIDTH = 116;

export interface SmartSelectPanelActions extends SmartSelectOptionActions {
  selectSmartSelectOutputPort: (node: go.Node, portKey: string) => void;
  commitSelectedSmartSelectOption: (node: go.Node) => void;
  scheduleFreePortHoverArrowsRemoval: (
    node: go.Node | null | undefined,
  ) => void;
  startSmartSelectPanelDrag: (
    node: go.Node,
    event: go.InputEvent,
    object: go.GraphObject,
  ) => void;
  moveSmartSelectPanel: (
    node: go.Node,
    event: go.InputEvent,
    object: go.GraphObject,
  ) => void;
  stopSmartSelectPanelDrag: (
    event: go.InputEvent,
    object: go.GraphObject,
  ) => void;
  removeSmartSelectOptionsPanel: (node: go.Node | null | undefined) => void;
  showSmartSelectOptionsPanel: (node: go.Node, port: go.GraphObject) => void;
}

// Event callbacks keep the view independent of the interaction controller.
export const createSmartSelectPanelView = (
  actions: SmartSelectPanelActions,
) => {
  const {
    selectSmartSelectOutputPort,
    commitSelectedSmartSelectOption,
    scheduleFreePortHoverArrowsRemoval,
    startSmartSelectPanelDrag,
    moveSmartSelectPanel,
    stopSmartSelectPanelDrag,
    removeSmartSelectOptionsPanel,
    showSmartSelectOptionsPanel,
  } = actions;
  const { getSmartSelectRecommendationElements } =
    createSmartSelectOptionCards(actions);
  const pages = new WeakMap<
    go.Node,
    Map<string, { options: SmartSelectRecommendedOption[]; page: number }>
  >();
  const getPageState = (node: go.Node, portKey: string) => {
    let portPages = pages.get(node);
    if (!portPages) {
      portPages = new Map();
      pages.set(node, portPages);
    }
    const options = getCachedSmartSelectOptions(node, portKey);
    let state = portPages.get(portKey);
    if (state?.options !== options) {
      state = { options, page: 0 };
      portPages.set(portKey, state);
    }
    return state;
  };
  const getPageCount = (state: { options: SmartSelectRecommendedOption[] }) =>
    Math.ceil(state.options.length / SMART_SELECT_OPTIONS_PER_PAGE);
  const getPaginationElements = (node: go.Node, portKey: string) => {
    const state = getPageState(node, portKey);
    const pageCount = getPageCount(state);
    if (
      getSmartSelectRecommendationStatus(node, portKey) !== 'ready' ||
      pageCount <= 1
    ) {
      return [];
    }
    return Array.from({ length: pageCount }, (_, page) => {
      const selected = state.page === page;
      const selectPage = (event: go.InputEvent) => {
        event.handled = true;
        clearTimer(hideTimers, node);
        getPageState(node, portKey).page = page;
        updateSmartSelectRecommendedOptions(node);
      };
      return $(
        go.Panel,
        'Auto',
        {
          name: `SMART_SELECT_PAGE_${page + 1}`,
          width: 20,
          height: 20,
          cursor: selected ? 'default' : 'pointer',
          isActionable: true,
          actionDown: (event: go.InputEvent) => {
            event.handled = true;
            clearTimer(hideTimers, node);
          },
          click: selectPage,
        },
        $(go.Shape, 'Rectangle', { fill: 'transparent', strokeWidth: 0 }),
        $(go.Shape, 'RoundedRectangle', {
          width: 16,
          height: 4,
          parameter1: 2,
          fill: selected ? '#101010' : '#D2D2D2',
          strokeWidth: 0,
        }),
      );
    });
  };
  const updatePageArrows = (panel: go.Part, node: go.Node, portKey: string) => {
    const state = getPageState(node, portKey);
    const pageCount = getPageCount(state);
    const shown =
      pageCount > 1 &&
      getSmartSelectRecommendationStatus(node, portKey) === 'ready';
    [SMART_SELECT_PREVIOUS_PAGE_NAME, SMART_SELECT_NEXT_PAGE_NAME].forEach(
      (name, index) => {
        const arrow = panel.findObject(name);
        if (!(arrow instanceof go.Panel)) return;
        // Opacity rather than visible/isEnabled keeps the arrow's layout slot
        // and keeps disabled arrows swallowing clicks.
        const enabled =
          index === 0 ? state.page > 0 : state.page < pageCount - 1;
        arrow.pickable = shown;
        arrow.opacity = !shown ? 0 : enabled ? 1 : 0.35;
        arrow.cursor = enabled ? 'pointer' : 'default';
      },
    );
  };
  const createPageArrow = (node: go.Node, direction: -1 | 1) => {
    const changePage = (event: go.InputEvent) => {
      event.handled = true;
      clearTimer(hideTimers, node);
      const portKey = nodeSmartSelectSelectedPortKeys.get(node);
      if (!portKey) return;
      const state = getPageState(node, portKey);
      const page = Math.max(
        0,
        Math.min(getPageCount(state) - 1, state.page + direction),
      );
      if (page === state.page) return;
      state.page = page;
      updateSmartSelectRecommendedOptions(node);
    };
    return $(
      go.Panel,
      'Auto',
      {
        name:
          direction === -1
            ? SMART_SELECT_PREVIOUS_PAGE_NAME
            : SMART_SELECT_NEXT_PAGE_NAME,
        alignment: direction === -1 ? go.Spot.Left : go.Spot.Right,
        width: 36,
        height: 36,
        cursor: 'pointer',
        isActionable: true,
        actionDown: (event: go.InputEvent) => {
          event.handled = true;
          clearTimer(hideTimers, node);
        },
        click: changePage,
      },
      $(go.Shape, 'Rectangle', { fill: 'transparent', strokeWidth: 0 }),
      $(go.Shape, {
        geometryString: direction === -1 ? 'M4 0 L0 4 L4 8' : 'M0 0 L4 4 L0 8',
        stroke: '#101010',
        strokeWidth: 1,
        fill: null,
      }),
    );
  };
  const setSmartSelectUseAndCloseEnabled = (
    node: go.Node,
    enabled: boolean,
  ) => {
    const panel = nodeSmartSelectPanels.get(node);
    const shape = panel?.findObject(SMART_SELECT_USE_BUTTON_SHAPE_NAME);
    const text = panel?.findObject(SMART_SELECT_USE_BUTTON_TEXT_NAME);

    if (shape instanceof go.Shape) {
      shape.fill = enabled
        ? SMART_SELECT_PLACE_BUTTON_FILL
        : 'rgba(0, 0, 0, 0.12)';
    }

    if (text instanceof go.TextBlock) {
      text.stroke = enabled ? 'white' : 'rgba(0, 0, 0, 0.38)';
    }
  };

  const createSmartSelectPill = (
    node: go.Node,
    port: AvailablePort,
    selected: boolean,
  ) => {
    const handleOutputAction = (event: go.InputEvent) => {
      event.handled = true;
      clearTimer(hideTimers, node);
      selectSmartSelectOutputPort(node, port.key);
    };

    const handleOutputActionDown = (event: go.InputEvent) => {
      event.handled = true;
      clearTimer(hideTimers, node);
    };

    return $(
      go.Panel,
      'Auto',
      {
        margin: new go.Margin(0, 4, 0, 0),
        cursor: selected ? 'default' : 'pointer',
        isActionable: true,
        actionDown: handleOutputActionDown,
        actionUp: handleOutputAction,
        click: handleOutputAction,
      },
      $(go.Shape, 'RoundedRectangle', {
        height: 24,
        parameter1: 12,
        fill: selected ? 'rgba(0, 0, 0, 0.54)' : 'rgba(0, 0, 0, 0.05)',
        stroke: 'transparent',
        strokeWidth: 0,
        isActionable: true,
        actionDown: handleOutputActionDown,
        actionUp: handleOutputAction,
        click: handleOutputAction,
      }),
      $(go.TextBlock, {
        text: getSmartSelectPortLabel(port),
        height: 18,
        margin: new go.Margin(3, 14),
        font: '400 13px Sandvik Sans Text',
        stroke: selected ? 'white' : 'rgba(0, 0, 0, 0.77)',
        verticalAlignment: go.Spot.Center,
        isActionable: true,
        actionDown: handleOutputActionDown,
        actionUp: handleOutputAction,
        click: handleOutputAction,
      }),
    );
  };

  const updateSmartSelectOutputPills = (node: go.Node) => {
    const panel = nodeSmartSelectPanels
      .get(node)
      ?.findObject(SMART_SELECT_OUTPUT_PILLS_PANEL_NAME);

    if (!(panel instanceof go.Panel)) {
      return;
    }

    const children: go.GraphObject[] = [];
    panel.elements.each((element) => {
      children.push(element);
    });
    children.forEach((element) => {
      panel.remove(element);
    });

    const selectedPortKey = nodeSmartSelectSelectedPortKeys.get(node);
    getSmartSelectOutputPorts(node).forEach((port) => {
      panel.add(
        createSmartSelectPill(node, port, port.key === selectedPortKey),
      );
    });
  };

  const createSmartSelectField = (
    label: string,
    value: string,
    valueTextName = '',
  ) =>
    $(
      go.Panel,
      'Vertical',
      {
        width: 270,
        margin: new go.Margin(0, 12, 18, 0),
        alignment: go.Spot.Left,
      },
      $(go.TextBlock, {
        text: label,
        height: 18,
        font: '400 12px Sandvik Sans Text',
        stroke: 'rgba(0, 0, 0, 0.55)',
        margin: new go.Margin(0, 0, 6, 0),
        alignment: go.Spot.Left,
        verticalAlignment: go.Spot.Center,
      }),
      $(
        go.Panel,
        'Auto',
        {
          width: 270,
          height: 38,
        },
        $(go.Shape, 'RoundedRectangle', {
          parameter1: 8,
          fill: 'white',
          stroke: 'rgba(0, 0, 0, 0.12)',
        }),
        $(go.TextBlock, {
          name: valueTextName,
          text: value,
          height: 20,
          margin: new go.Margin(8, 10),
          font: '400 14px Sandvik Sans Text',
          stroke: 'rgba(0, 0, 0, 0.77)',
          alignment: go.Spot.Left,
          verticalAlignment: go.Spot.Center,
        }),
      ),
    );

  const updateSmartSelectParticleSize = (
    node: go.Node,
    port: AvailablePort,
  ) => {
    const text = nodeSmartSelectPanels
      .get(node)
      ?.findObject(SMART_SELECT_PARTICLE_SIZE_TEXT_NAME);

    if (text instanceof go.TextBlock) {
      text.text = getSmartSelectParticleSize(port);
    }

    const capacityText = nodeSmartSelectPanels
      .get(node)
      ?.findObject(SMART_SELECT_CAPACITY_TEXT_NAME);
    if (capacityText instanceof go.TextBlock) {
      capacityText.text = getSmartSelectCapacity(port);
    }
  };

  const replacePanelElements = (
    panel: go.Panel,
    elements: go.GraphObject[],
  ) => {
    const currentElements: go.GraphObject[] = [];
    panel.elements.each((element) => {
      currentElements.push(element);
    });
    currentElements.forEach((element) => {
      panel.remove(element);
    });
    elements.forEach((element) => {
      panel.add(element);
    });
  };

  const updateSmartSelectRecommendedOptions = (node: go.Node) => {
    const selectedPortKey = nodeSmartSelectSelectedPortKeys.get(node);
    const part = nodeSmartSelectPanels.get(node);
    const panel = part?.findObject(SMART_SELECT_RECOMMENDED_OPTIONS_PANEL_NAME);

    if (!part || !(panel instanceof go.Panel) || !selectedPortKey) {
      return;
    }

    const pagination = part.findObject(SMART_SELECT_PAGINATION_PANEL_NAME);
    const updatePanels = () => {
      replacePanelElements(
        panel,
        getSmartSelectRecommendationElements(
          node,
          selectedPortKey,
          getPageState(node, selectedPortKey).page,
        ),
      );
      if (pagination instanceof go.Panel) {
        replacePanelElements(
          pagination,
          getPaginationElements(node, selectedPortKey),
        );
      }
      updatePageArrows(part, node, selectedPortKey);
    };

    if (panel.diagram) {
      panel.diagram.commit(updatePanels, 'update smart select recommendations');
      return;
    }

    updatePanels();
  };

  const createSmartSelectUseAndCloseButton = (node: go.Node) => {
    const handleUseAction = (event: go.InputEvent) => {
      event.handled = true;
      commitSelectedSmartSelectOption(node);
    };

    const handleUseActionDown = (event: go.InputEvent) => {
      event.handled = true;
      clearTimer(hideTimers, node);
    };

    return $(
      go.Panel,
      'Auto',
      {
        position: new go.Point(
          SMART_SELECT_PANEL_WIDTH - 48 - USE_BUTTON_WIDTH,
          0,
        ),
        cursor: 'pointer',
        isActionable: true,
        actionDown: handleUseActionDown,
        actionUp: handleUseAction,
        click: handleUseAction,
      },
      $(go.Shape, 'RoundedRectangle', {
        name: SMART_SELECT_USE_BUTTON_SHAPE_NAME,
        width: USE_BUTTON_WIDTH,
        height: 36,
        parameter1: 18,
        fill: 'rgba(0, 0, 0, 0.12)',
        stroke: 'transparent',
        strokeWidth: 0,
        isActionable: true,
        actionDown: handleUseActionDown,
        actionUp: handleUseAction,
        click: handleUseAction,
      }),
      $(go.TextBlock, {
        name: SMART_SELECT_USE_BUTTON_TEXT_NAME,
        text: 'Use and close',
        font: '400 13px Sandvik Sans Text',
        stroke: 'rgba(0, 0, 0, 0.38)',
        isActionable: true,
        actionDown: handleUseActionDown,
        actionUp: handleUseAction,
        click: handleUseAction,
      }),
    );
  };

  const createSmartSelectOptionsPanel = (
    node: go.Node,
    port: go.GraphObject,
  ) => {
    const panelDimensions = getSmartSelectPanelDimensions(node.diagram);
    const outputPorts = getSmartSelectOutputPorts(node);
    const selectedPortKey = getSelectedSmartSelectPortKey(node, port);
    const selectedPort = outputPorts.find(
      (outputPort) => outputPort.key === selectedPortKey,
    );
    // Each newly opened panel starts on the first page.
    pages.delete(node);

    const optionsPanel = $(
      go.Part,
      'Auto',
      {
        layerName: 'Tool',
        zOrder: 3,
        selectable: false,
        pickable: true,
        position: getSmartSelectPanelPosition(node, port),
        scale: panelDimensions.scale,
        isInDocumentBounds: false,
        mouseEnter: () => {
          clearTimer(hideTimers, node);
        },
        mouseLeave: () => {
          scheduleFreePortHoverArrowsRemoval(node);
        },
      },
      $(go.Shape, 'RoundedRectangle', {
        width: SMART_SELECT_PANEL_WIDTH,
        height: SMART_SELECT_PANEL_HEIGHT,
        parameter1: 8,
        fill: 'white',
        stroke: theme.palette.info.main,
        strokeWidth: 1,
      }),
      $(
        go.Panel,
        'Vertical',
        {
          width: SMART_SELECT_PANEL_WIDTH - 48,
          height: SMART_SELECT_PANEL_HEIGHT - 48,
          margin: new go.Margin(24),
        },
        $(
          go.Panel,
          'Spot',
          {
            width: SMART_SELECT_PANEL_WIDTH - 48,
            height: 24,
            cursor: 'move',
            margin: new go.Margin(0, 0, 16, 0),
            isActionable: true,
            actionDown: (event: go.InputEvent, obj: go.GraphObject) => {
              startSmartSelectPanelDrag(node, event, obj);
            },
            actionMove: (event: go.InputEvent, obj: go.GraphObject) => {
              moveSmartSelectPanel(node, event, obj);
            },
            actionUp: stopSmartSelectPanelDrag,
            actionCancel: stopSmartSelectPanelDrag,
          },
          $(go.TextBlock, {
            text: 'Smart selections',
            font: '600 15px Sandvik Sans Text',
            stroke: 'rgba(0, 0, 0, 0.77)',
            alignment: go.Spot.Left,
          }),
          $(
            go.Panel,
            'Spot',
            {
              width: 40,
              height: 32,
              cursor: 'pointer',
              alignment: new go.Spot(1, 0.5, -8, 0),
              isActionable: true,
              actionDown: (event: go.InputEvent) => {
                event.handled = true;
              },
              actionUp: (event: go.InputEvent) => {
                event.handled = true;
                removeSmartSelectOptionsPanel(node);
              },
              click: (event: go.InputEvent) => {
                event.handled = true;
                removeSmartSelectOptionsPanel(node);
              },
            },
            $(go.Shape, 'Rectangle', {
              width: 40,
              height: 32,
              fill: 'transparent',
              stroke: 'transparent',
            }),
            $(go.Picture, {
              source: SMART_SELECT_CLOSE_ICON_SOURCE,
              desiredSize: new go.Size(14, 14),
              alignment: go.Spot.Center,
            }),
          ),
        ),
        $(go.TextBlock, {
          text: 'Output',
          height: 18,
          font: '400 12px Sandvik Sans Text',
          stroke: 'rgba(0, 0, 0, 0.55)',
          margin: new go.Margin(0, 0, 8, 0),
          alignment: go.Spot.Left,
          verticalAlignment: go.Spot.Center,
        }),
        $(
          go.Panel,
          'Horizontal',
          {
            name: SMART_SELECT_OUTPUT_PILLS_PANEL_NAME,
            margin: new go.Margin(0, 0, 18, 0),
            alignment: go.Spot.Left,
          },
          ...outputPorts.map((outputPort) =>
            createSmartSelectPill(
              node,
              outputPort,
              outputPort.key === selectedPortKey,
            ),
          ),
        ),
        $(
          go.Panel,
          'Horizontal',
          { alignment: go.Spot.Left },
          createSmartSelectField(
            'Particle size',
            selectedPort ? getSmartSelectParticleSize(selectedPort) : 'Any',
            SMART_SELECT_PARTICLE_SIZE_TEXT_NAME,
          ),
          createSmartSelectField(
            'Capacity',
            selectedPort ? getSmartSelectCapacity(selectedPort) : 'Any',
            SMART_SELECT_CAPACITY_TEXT_NAME,
          ),
        ),
        $(go.TextBlock, {
          text: 'Recommended options',
          height: 19,
          font: '400 13px Sandvik Sans Text',
          stroke: 'rgba(0, 0, 0, 0.55)',
          margin: new go.Margin(0, 0, 10, 0),
          alignment: go.Spot.Left,
        }),
        $(
          go.Panel,
          'Horizontal',
          {
            width: SMART_SELECT_PANEL_WIDTH - 48,
            height: SMART_SELECT_OPTION_CARD_HEIGHT,
          },
          createPageArrow(node, -1),
          $(
            go.Panel,
            'Horizontal',
            {
              name: SMART_SELECT_RECOMMENDED_OPTIONS_PANEL_NAME,
              width: SMART_SELECT_PANEL_WIDTH - 168,
              height: SMART_SELECT_OPTION_CARD_HEIGHT,
              margin: new go.Margin(0, 24),
            },
            ...getSmartSelectRecommendationElements(
              node,
              selectedPortKey,
              getPageState(node, selectedPortKey).page,
            ),
          ),
          createPageArrow(node, 1),
        ),
        $(
          go.Panel,
          'Horizontal',
          {
            name: SMART_SELECT_PAGINATION_PANEL_NAME,
            height: 20,
          },
          ...getPaginationElements(node, selectedPortKey),
        ),
        $(
          go.Panel,
          'Position',
          {
            width: SMART_SELECT_PANEL_WIDTH - 48,
            height: 36,
            alignment: go.Spot.Right,
          },
          createSmartSelectUseAndCloseButton(node),
        ),
      ),
    );
    updatePageArrows(optionsPanel, node, selectedPortKey);
    return optionsPanel;
  };

  const createSmartSelectButton = (node: go.Node, port: go.GraphObject) => {
    const arrowPoints = getArrowPoints(node, port);

    if (!arrowPoints) {
      return null;
    }

    const handleButtonAction = (event: go.InputEvent) => {
      event.handled = true;
      clearTimer(hideTimers, node);
      showSmartSelectOptionsPanel(node, port);
    };

    const handleButtonActionDown = (event: go.InputEvent) => {
      event.handled = true;
      clearTimer(hideTimers, node);
    };

    const button = $(
      go.Part,
      'Auto',
      {
        layerName: 'Tool',
        selectable: false,
        pickable: true,
        isActionable: true,
        cursor: 'pointer',
        position: getSmartSelectButtonPosition(
          arrowPoints.end,
          arrowPoints.direction,
        ),
        isInDocumentBounds: false,
        mouseEnter: () => {
          clearTimer(hideTimers, node);
        },
        mouseLeave: () => {
          scheduleFreePortHoverArrowsRemoval(node);
        },
        actionDown: handleButtonActionDown,
        actionUp: handleButtonAction,
        click: handleButtonAction,
      },
      $(go.Shape, 'RoundedRectangle', {
        width: SMART_SELECT_BUTTON_WIDTH,
        height: SMART_SELECT_BUTTON_HEIGHT,
        parameter1: SMART_SELECT_BUTTON_HEIGHT / 2,
        fill: 'white',
        stroke: theme.palette.info.main,
        strokeWidth: 1,
        isActionable: true,
        actionDown: handleButtonActionDown,
        actionUp: handleButtonAction,
        click: handleButtonAction,
      }),
      $(
        go.Panel,
        'Horizontal',
        {
          alignment: go.Spot.Left,
          margin: new go.Margin(0, 12),
          isActionable: true,
          actionDown: handleButtonActionDown,
          actionUp: handleButtonAction,
          click: handleButtonAction,
        },
        $(go.Picture, {
          source: SMART_SELECT_ICON_SOURCE,
          desiredSize: new go.Size(16, 16),
          margin: new go.Margin(0, 8, 0, 0),
          isActionable: true,
          actionDown: handleButtonActionDown,
          actionUp: handleButtonAction,
          click: handleButtonAction,
        }),
        $(go.TextBlock, {
          text: 'Explore Smart selections',
          font: '400 13px Sandvik Sans Text',
          stroke: 'rgba(0, 0, 0, 0.77)',
          verticalAlignment: go.Spot.Center,
          isActionable: true,
          actionDown: handleButtonActionDown,
          actionUp: handleButtonAction,
          click: handleButtonAction,
        }),
      ),
    );

    return button;
  };

  return {
    setSmartSelectUseAndCloseEnabled,
    updateSmartSelectOutputPills,
    updateSmartSelectParticleSize,
    updateSmartSelectRecommendedOptions,
    createSmartSelectOptionsPanel,
    createSmartSelectButton,
  };
};
